import { TelegramLinkCard } from "../components/TelegramLinkCard";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wallet, ArrowDownToLine, ArrowUpFromLine, Gift, Users, Trophy,
  Ticket, Receipt, Bell, Headphones, Languages, LogOut, ChevronRight, Volume2, Vibrate,
  Copy, ShieldCheck, Crown, Phone, Siren, BellRing, Loader2,
  BadgeIndianRupee, MessageSquare , ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n, LANGUAGES } from "../i18n";
import { LanguagePicker } from "../components/LanguagePicker";
import { MyAgentCard } from "../components/MyAgentCard";
import { CustomerIdCard } from "../components/CustomerIdCard";
import { FX_OPTIONS, getFxCurrency, setFxCurrency } from "../lib/fx";
import api from "../lib/api";
import { fmt, maskMobile, dateLabel } from "../lib/format";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "../lib/push";
import { chimeEnabled, playWinChime } from "../lib/winChime";
import { QuietHoursRow } from "../components/QuietHoursRow";
import { StreakBadgeWall } from "../components/StreakBadgeWall";
import { Music4 } from "lucide-react";
import { InstallAppButton } from "../components/InstallAppButton";
import { StreakLeaderboard } from "../components/StreakLeaderboard";
import { SavedMethods, useSavedMethods } from "../components/SavedMethods";
import { toast } from "sonner";
import { isMuted, setMuted, sfx, isAmbienceOn, setAmbience } from "../lib/sound";
import { hapticsEnabled, setHaptics, haptic } from "../lib/haptics";
import { NicknameEditor } from "../components/NicknameEditor";
import { PushTestCard } from "../components/PushTestCard";

const MENU = [
  { icon: ArrowDownToLine, key: "recharge", to: "/recharge" },
  { icon: ArrowUpFromLine, key: "withdraw", to: "/withdraw" },
  { icon: Gift, key: "gift_code", to: "/gift-code" },
  { icon: Users, key: "referral_commission", to: "/referral" },
  { icon: Trophy, key: "result_history_menu", to: "/result" },
  { icon: Crown, key: "leaderboard", to: "/leaderboard" },
  { icon: Ticket, key: "my_bets", to: "/my-bets" },
  { icon: BadgeIndianRupee, key: "rate_card", to: "/rate-card" },
  { icon: Receipt, key: "transactions", to: "/transactions" },
  { icon: ArrowDownToLine, key: "recharge_records", to: "/recharge?tab=records" },
];

export default function Me() {
  const saved = useSavedMethods();
  const { user, wallet, logout, loadUser } = useAuth();
  const [photoBusy, setPhotoBusy] = useState(false);
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPhotoBusy(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post("/auth/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); await loadUser?.(); toast.success(lang === "ta" ? "புகைப்படம் சேமிக்கப்பட்டது — live table-ல் உங்கள் முகம் தெரியும்" : "Photo saved — your face now appears at live tables"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setPhotoBusy(false); e.target.value = ""; }
  };
  const { lang, changeLang, t } = useI18n();
  const [soundOn, setSoundOn] = useState(!isMuted());
  const [vibOn, setVibOn] = useState(hapticsEnabled());
  const toggleSound = () => { const on = !soundOn; setMuted(!on); setSoundOn(on); if (on) sfx.win(); };
  const [ambOn, setAmbOn] = useState(isAmbienceOn());
  const toggleAmb = () => { const on = !ambOn; setAmbience(on); setAmbOn(on); if (on) sfx.chipToss(); };
  const toggleVib = () => { const on = !vibOn; setHaptics(on); setVibOn(on); if (on) haptic.land(); };
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [chimeOn, setChimeOn] = useState(chimeEnabled());
  const [pushBusy, setPushBusy] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => { isPushEnabled().then(setPushOn).catch(() => {}); }, []);

  useEffect(() => {
    if (user === false) navigate("/auth");
  }, [user, navigate]);

  const togglePush = useCallback(async () => {
    setPushBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success(t("push_notifications") + " ✕"); }
      else { await enablePush(); setPushOn(true); toast.success(t("notifications_on")); }
    } catch (err) {
      toast.error(err.message || "Could not update notifications");
    } finally { setPushBusy(false); }
  }, [pushOn, t]);

  if (!user) {
    return null;
  }

  const copyRef = () => {
    navigator.clipboard.writeText(user.referral_code || "");
    toast.success("Referral code copied!");
  };

  const care = config?.customer_care_number;
  const vipPct = config?.vip_recharge_bonus_pct ?? 2;
  const emergency = config?.emergency_number;

  return (
    <div className="space-y-4 pb-6">
      {/* Profile card */}
      <div className="rl-card p-4" data-testid="profile-card">
        <div className="flex items-center gap-3">
          <label className="relative w-14 h-14 rounded-2xl rl-gold-bg flex items-center justify-center overflow-hidden cursor-pointer active:scale-95 transition-transform" data-testid="me-photo-picker" title="Change photo">
            {user.photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${user.photo_url}`} alt="" className="w-full h-full object-cover" data-testid="me-photo-img" /> : <Crown className="w-7 h-7" />}
            <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[8px] font-bold text-center py-0.5">{photoBusy ? "…" : "📷 " + (lang === "ta" ? "புகைப்படம்" : "PHOTO")}</span>
            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={photoBusy} data-testid="me-photo-input" />
          </label>
          <div className="flex-1">
            <div className="font-display font-black text-slate-900 text-lg flex items-center gap-2">{user.name}{user.tags?.includes("VIP") && <span className="text-[10px] font-black bg-gradient-to-r from-amber-400 to-yellow-300 text-slate-900 rounded-full px-2 py-0.5 shadow" data-testid="me-vip-badge">★ VIP</span>}</div>
            <div className="text-xs text-slate-500">{maskMobile(user.mobile)}</div>
            <NicknameEditor user={user} ta={lang === "ta"} onSaved={loadUser} />
            <div className="text-[10px] text-slate-500">{t("joined")} {dateLabel(user.created_at)}</div>
          </div>
          {user.role === "admin" && (
            <button onClick={() => navigate("/admin")} className="rl-card px-3 py-1.5 text-xs text-blue-600 flex items-center gap-1" data-testid="me-admin-btn"><ShieldCheck className="w-3.5 h-3.5" />{t("admin")}</button>
          )}
          {user.role === "agent" && (
            <button onClick={() => navigate("/agent")} className="rounded-xl px-3 py-1.5 text-xs font-black text-white flex items-center gap-1 active:scale-95 transition-transform" style={{ background: "linear-gradient(120deg,#1d4ed8,#0f172a)" }} data-testid="me-agent-btn"><ShieldCheck className="w-3.5 h-3.5" />{lang === "ta" ? "Agent Panel" : "Agent Panel"}</button>
          )}
        </div>
        {user.tags?.includes("VIP") && (
          <div className="mt-3 rounded-xl bg-slate-900 text-amber-100 p-3 text-[11px]" data-testid="me-vip-perks">
            <div className="font-black text-amber-300 text-xs mb-1">★ VIP {lang === "ta" ? "சலுகைகள்" : "perks"}</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-white/5 p-2"><div className="text-base">🎁</div>{lang === "ta" ? `Recharge-க்கு +${vipPct}% bonus` : `+${vipPct}% on every recharge`}</div>
              <div className="rounded-lg bg-white/5 p-2"><div className="text-base">⚡</div>{lang === "ta" ? "Priority withdrawal" : "Priority withdrawals"}</div>
              <div className="rounded-lg bg-white/5 p-2"><div className="text-base">👑</div>{lang === "ta" ? "VIP badge & alerts" : "VIP badge & alerts"}</div>
            </div>
          </div>
        )}
        <button onClick={copyRef} className="mt-3 w-full flex items-center justify-between rl-card px-3 py-2" data-testid="me-referral-code">
          <span className="text-xs text-slate-500">{t("referral_code")}</span>
          <span className="flex items-center gap-1 font-mono font-bold text-blue-600 text-sm">{user.referral_code}<Copy className="w-3.5 h-3.5" /></span>
        </button>
      </div>

      {/* Wallet card */}
      <MyAgentCard />
      <CustomerIdCard user={user} />
      {window.matchMedia("(display-mode: standalone)").matches && (
        <a href={`${window.location.origin}/?from=app`} target="_blank" rel="noreferrer" className="rl-card p-3 flex items-center justify-between" data-testid="open-website-btn">
          <div><div className="text-sm font-semibold text-slate-900">{lang === "ta" ? "Website-ல திற" : "Open website"}</div><div className="text-[11px] text-slate-500">{lang === "ta" ? "Browser-ல ROYAL LAKSHMI site" : "ROYAL LAKSHMI site in your browser"}</div></div>
          <ExternalLink className="w-4 h-4 text-blue-600" />
        </a>
      )}
      <TelegramLinkCard ta={lang === "ta"} variant="customer" />
      <div className="rl-card p-4" data-testid="wallet-card">
        <div className="flex items-center gap-2 mb-3"><Wallet className="w-4 h-4 text-blue-600" /><span className="font-display font-bold text-slate-900">{t("my_wallet")}</span></div>
        <div className="bg-slate-100 rounded-xl p-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] text-slate-500">{t("wallet_balance")}</div>
            <div className="font-mono font-black text-slate-900 text-2xl" data-testid="me-total-balance">{fmt(wallet?.total)}</div>
            {wallet?.pending_withdrawal > 0 && <div className="text-[10px] font-bold text-indigo-700" data-testid="me-pending-withdrawal">🏦 {fmt(wallet.pending_withdrawal)} {lang === "ta" ? "withdraw request-ல் இருக்கு (wallet-ல் இருந்து எடுத்தாச்சு)" : "in your withdrawal request (already taken from wallet)"}</div>}
          </div>
          <div className="text-[10px] text-slate-500 text-right">{lang === "ta" ? "Booking · Games · Withdraw" : "Booking · Games · Withdraw"}<br />{lang === "ta" ? "எல்லாம் இந்த ஒரு balance-ல்" : "all from this one balance"}</div>
        </div>
        <button onClick={() => navigate("/my-account")} className="mt-3 w-full flex items-center justify-between rounded-xl bg-slate-900 text-white px-3 py-2.5 active:scale-[.98] transition-transform" data-testid="me-my-account-btn">
          <div className="text-left"><div className="text-sm font-black text-amber-300">{lang === "ta" ? "என் கணக்கு — முழு விவரம்" : "My Account — full details"}</div><div className="text-[10px] text-slate-300">{lang === "ta" ? "Payment · Tickets · Result · Winning · எடுக்கக்கூடியது" : "Payments · Tickets · Results · Winnings · Withdrawable"}</div></div>
          <span className="text-amber-300 font-black">›</span>
        </button>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <button onClick={() => navigate("/recharge")} className="rl-gold-bg font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-1" data-testid="me-recharge-btn"><ArrowDownToLine className="w-4 h-4" />{t("recharge")}</button>
          <button onClick={() => navigate("/withdraw")} className="rl-card font-bold py-2.5 rounded-xl text-sm text-slate-900 flex items-center justify-center gap-1" data-testid="me-withdraw-btn"><ArrowUpFromLine className="w-4 h-4 text-blue-600" />{t("withdraw")}</button>
        </div>
      </div>

      {/* Install App (persistent, always available for install/reinstall) */}
      <StreakBadgeWall ta={lang === "ta"} />
      <StreakLeaderboard />

      <InstallAppButton />
      <button onClick={() => navigate("/app")} className="w-full rl-card p-3 flex items-center gap-3 text-left" data-testid="me-apk-link">
        <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-black text-xs">APK</div>
        <div className="flex-1"><div className="font-semibold text-slate-900 text-sm">Android App (APK) download</div><div className="text-[11px] text-slate-500">நண்பர்களுக்கு share பண்ண link-ம் இங்கே</div></div>
      </button>

      {/* Saved payout methods */}
      <div data-testid="me-payout-methods">
        <h2 className="font-display font-bold text-slate-900 mb-2 flex items-center gap-2">Saved Payout Methods <span className="text-[10px] font-bold text-slate-400">{saved.methods.length}/8</span></h2>
        {saved.methods.length > 1 && <p className="text-[10px] text-slate-500 -mt-1 mb-2">Tap ★ to make one the default — it will be pre-selected on the Withdraw page.</p>}
        <SavedMethods methods={saved.methods} onRemove={saved.remove} onSetDefault={saved.setDefault} manage />
      </div>

      {/* Contact / Call buttons */}
      {(care || emergency) && (
        <div className="grid grid-cols-2 gap-3" data-testid="me-call-buttons">
          {care && (
            <a href={`tel:${care}`} data-testid="me-customer-care-btn"
              className="rl-card p-3.5 flex items-center gap-3 active:scale-95 transition-transform">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-white" /></div>
              <div className="min-w-0">
                <div className="text-xs font-bold text-slate-900 truncate">{t("customer_care")}</div>
                <div className="text-[11px] text-blue-600 font-mono">{care}</div>
              </div>
            </a>
          )}
          {emergency && (
            <a href={`tel:${emergency}`} data-testid="me-emergency-btn"
              className="rl-card p-3.5 flex items-center gap-3 active:scale-95 transition-transform">
              <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center shrink-0"><Siren className="w-5 h-5 text-white" /></div>
              <div className="min-w-0">
                <div className="text-xs font-bold text-slate-900 truncate">{t("emergency")}</div>
                <div className="text-[11px] text-red-500 font-mono">{emergency}</div>
              </div>
            </a>
          )}
        </div>
      )}

      {/* Menu */}
      <div className="rl-card overflow-hidden">
        {MENU.map((m, i) => (
          <button key={i} onClick={() => navigate(m.to)} data-testid={`menu-${m.key}`}
            className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-200 last:border-0 active:bg-white/5">
            <m.icon className="w-4.5 h-4.5 text-blue-600" />
            <span className="text-sm text-slate-900 flex-1 text-left">{t(m.key)}</span>
            <ChevronRight className="w-4 h-4 text-slate-500" />
          </button>
        ))}
      </div>

      {/* Settings */}
      <div className="rl-card overflow-hidden">
        <button onClick={() => setLangOpen(true)} className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-200 active:bg-white/5" data-testid="me-language-btn">
          <Languages className="w-4.5 h-4.5 text-blue-600" />
          <span className="text-sm text-slate-900 flex-1 text-left">{t("language")}</span>
          <span className="text-xs text-slate-500">{LANGUAGES.find((l) => l.code === lang)?.native || "English"}</span>
        </button>
        <div className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-200" data-testid="me-sound-row">
          <Volume2 className={`w-4.5 h-4.5 ${soundOn ? "text-emerald-500" : "text-blue-600"}`} />
          <span className="text-sm text-slate-900 flex-1 text-left">{t("game_sounds")}</span>
          <button onClick={toggleSound} data-testid="me-sound-toggle" className={`w-11 h-6 rounded-full relative transition-colors ${soundOn ? "bg-emerald-500" : "bg-slate-300"}`}><span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${soundOn ? "left-[22px]" : "left-0.5"}`} /></button>
        </div>
        <div className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-200" data-testid="me-vibration-row">
          <Vibrate className={`w-4.5 h-4.5 ${vibOn ? "text-emerald-500" : "text-blue-600"}`} />
          <span className="text-sm text-slate-900 flex-1 text-left">{t("vibration")}</span>
          <button onClick={toggleVib} data-testid="me-vibration-toggle" className={`w-11 h-6 rounded-full relative transition-colors ${vibOn ? "bg-emerald-500" : "bg-slate-300"}`}><span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${vibOn ? "left-[22px]" : "left-0.5"}`} /></button>
        </div>
        <div className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-200" data-testid="me-ambience-row">
          <Headphones className={`w-4.5 h-4.5 ${ambOn ? "text-emerald-500" : "text-blue-600"}`} />
          <span className="text-sm text-slate-900 flex-1 text-left">{t("table_ambience")}</span>
          <button onClick={toggleAmb} data-testid="me-ambience-toggle" className={`w-11 h-6 rounded-full relative transition-colors ${ambOn ? "bg-emerald-500" : "bg-slate-300"}`}><span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${ambOn ? "left-[22px]" : "left-0.5"}`} /></button>
        </div>
        {pushSupported() && (
          <button onClick={togglePush} disabled={pushBusy} className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-200 active:bg-white/5" data-testid="me-push-toggle-btn">
            <BellRing className={`w-4.5 h-4.5 ${pushOn ? "text-emerald-500" : "text-blue-600"}`} />
            <span className="text-sm text-slate-900 flex-1 text-left">{t("push_notifications")}</span>
            {pushBusy ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              : <span className={`text-xs font-semibold ${pushOn ? "text-emerald-500" : "text-slate-500"}`}>{pushOn ? t("notifications_on") : t("enable_notifications")}</span>}
          </button>
        )}
        <button onClick={() => { const off = chimeEnabled(); localStorage.setItem("rl_win_chime", off ? "off" : "on"); setChimeOn(!off); if (!off) playWinChime(true); }} className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-200 active:bg-white/5" data-testid="me-win-chime-toggle">
          <Music4 className={`w-4.5 h-4.5 ${chimeOn ? "text-emerald-500" : "text-slate-400"}`} />
          <span className="text-sm text-slate-900 flex-1 text-left">{lang === "ta" ? "வெற்றி chime & vibration" : "Win chime & vibration"}</span>
          <span className={`text-xs font-semibold ${chimeOn ? "text-emerald-500" : "text-slate-500"}`}>{chimeOn ? "ON" : "OFF"}</span>
        </button>
        <button onClick={async () => { try { const on = !user.result_sms; await api.put("/auth/prefs", { result_sms: on }); toast.success(on ? (lang === "ta" ? "Result SMS ON — book பண்ண draw-ன் result SMS-ல் வரும்" : "Result SMS on — you'll get an SMS for every draw you book") : (lang === "ta" ? "Result SMS OFF" : "Result SMS off")); loadUser && loadUser(); } catch { toast.error("Could not update"); } }}
          className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-slate-200 active:bg-white/5" data-testid="me-result-sms-toggle">
          <MessageSquare className={`w-4.5 h-4.5 ${user.result_sms ? "text-emerald-500" : "text-slate-400"}`} />
          <span className="text-sm text-slate-900 flex-1 text-left">{lang === "ta" ? "Result SMS (push இல்லாத phone-க்கு)" : "Result SMS (for phones without push)"}<div className="text-[10px] text-slate-500">{lang === "ta" ? "நீங்கள் book பண்ண ஒவ்வொரு draw-ன் result-ம் SMS-ல்" : "Every draw you book — result by SMS when push is off"}</div></span>
          <span className={`text-xs font-semibold ${user.result_sms ? "text-emerald-500" : "text-slate-500"}`} data-testid="me-result-sms-state">{user.result_sms ? "ON" : "OFF"}</span>
        </button>
        <QuietHoursRow ta={lang === "ta"} />
        <div className="px-3 py-3 border-b border-slate-200"><PushTestCard ta={lang === "ta"} /></div>
        <button onClick={() => window.open(config?.support_link || (care ? `tel:${care}` : "https://wa.me/919999999999"), "_blank")} className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-white/5" data-testid="me-support-btn">
          <Headphones className="w-4.5 h-4.5 text-blue-600" />
          <span className="text-sm text-slate-900 flex-1 text-left">{t("customer_support")}</span>
          <ChevronRight className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      <button onClick={() => { logout(); navigate("/"); toast.success("Logged out"); }} className="w-full rl-card py-3.5 rounded-2xl text-red-400 font-semibold flex items-center justify-center gap-2" data-testid="me-logout-btn">
        <LogOut className="w-4 h-4" />{t("logout")}
      </button>

      <p className="text-center text-[10px] text-slate-500">User ID: {user.id.slice(0, 8)} · 18+ · Play Responsibly</p>

      {langOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setLangOpen(false)} data-testid="language-picker">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm p-5 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-black text-lg mb-3 text-slate-900">{t("choose_language")}</div>
            <LanguagePicker compact onPick={(code) => { setLangOpen(false); toast.success(LANGUAGES.find((l) => l.code === code)?.native || code); }} />
            <div className="mt-3 border-t border-slate-100 pt-3" data-testid="fx-currency-card">
              <div className="text-xs font-bold text-slate-700 mb-1">Currency hint · ₹ ≈ ?</div>
              <select value={getFxCurrency()} onChange={(e) => { setFxCurrency(e.target.value); localStorage.setItem("rl_fx_manual", "1"); setLangOpen(false); toast.success(e.target.value ? `Amounts will also show ≈ ${e.target.value}` : "₹ only"); }} className="w-full rl-card px-3 py-2 text-sm outline-none" data-testid="fx-currency-select">
                {FX_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <p className="text-[10px] text-slate-400 mt-1">Approximate only — all play, recharge and payout is in ₹ (INR).</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
